import type { FC } from "react"
import { matchPath, useLocation } from "react-router"
import { Helmet } from "react-helmet-async"

interface Props {
  title?: string
  description?: string
}

const TITLE_PREFIX = "WarpMe for Tenpo"
const DEFAULT_DESCRIPTION =
  "WarpMe for Tenpo helps venue teams manage tickets, sales, and operations."
const NOT_FOUND_DESCRIPTION = "お探しのページは存在しないか、移動しました。"

const resolveDefaultTitle = (pathname: string) => {
  const routeTitles = [
    { path: "/login", title: "ログイン" },
    { path: "/login/forget-password", title: "パスワード再設定" },
    { path: "/login/forget-password/sent", title: "メール送信完了" },
    { path: "/login/reset-password", title: "新しいパスワード設定" },
    { path: "/signup", title: "店舗アカウント登録" },
    { path: "/signup/email-not-verified", title: "メール認証" },
    { path: "/signup/info", title: "店舗情報登録" },
    { path: "/dashboard", title: "ダッシュボード" },
    { path: "/tickets", title: "チケット" },
    { path: "/tickets/add", title: "チケット追加" },
    { path: "/tickets/preview", title: "プレビュー" },
    { path: "/tickets/:ticketId", title: "チケット詳細" },
    { path: "/tickets/:ticketId/edit", title: "チケット編集" },
    { path: "/sales", title: "売上" },
    { path: "/setting/shop", title: "店舗情報" },
    { path: "/setting/members", title: "メンバー" },
    { path: "/setting/members/add", title: "メンバーを追加" },
    { path: "/setting/members/edit/:memberId", title: "メンバー編集" },
    { path: "/setting/tickets", title: "チケットもぎり方法" },
    { path: "/not-found", title: "ページが見つかりません" },
  ]

  return routeTitles.find(({ path }) => matchPath({ path, end: true }, pathname))?.title
}

export const MetaTags: FC<Props> = ({ title, description }) => {
  const { pathname } = useLocation()
  const resolvedTitle = title ?? resolveDefaultTitle(pathname) ?? TITLE_PREFIX
  const resolvedDescription =
    description ?? (pathname === "/not-found" ? NOT_FOUND_DESCRIPTION : DEFAULT_DESCRIPTION)

  return (
    <Helmet>
      <title>
        {resolvedTitle === TITLE_PREFIX ? TITLE_PREFIX : `${TITLE_PREFIX} | ${resolvedTitle}`}
      </title>
      <meta name="description" content={resolvedDescription} />
      <meta property="og:site_name" content={TITLE_PREFIX} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
    </Helmet>
  )
}
